import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaBoxOpen, FaMoneyBillAlt, FaUtensils, FaCashRegister, FaArrowLeft, FaSignOutAlt, FaStore, FaTrashAlt, FaChartLine } from 'react-icons/fa';

import Inventario from './Inventario';
import Egresos from './Egresos';
import Productos from './Productos';
import Ventas from './Ventas';
import MisVentas from './MisVentas';
import Mermas from './Mermas';

export default function ModuloSucursal({ user, logout }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [view, setView] = useState('menu');
    const [sucursalName, setSucursalName] = useState('');
    const [sucursalConfig, setSucursalConfig] = useState({});

    useEffect(() => {
        const config = {
            1: {
                nombre: 'COMO EN CASA',
                gradient: 'linear-gradient(135deg, #D31027 0%, #EA384D 100%)', // ROJO
                icon: <FaStore />,
                color: '#D31027',
                description: 'Sucursal Principal'
            },
            2: {
                nombre: 'YUM YUM SUC. 1',
                gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', // TURQUESA
                icon: <FaStore />,
                color: '#11998e',
                description: 'Zona Centro'
            },
            3: {
                nombre: 'YUM YUM SUC. 2',
                gradient: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)', // AMARILLO
                icon: <FaStore />,
                color: '#f7971e',
                description: 'Zona Norte'
            }
        };

        setSucursalConfig(config[id] || {
            nombre: 'Sucursal Desconocida',
            gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            icon: <FaStore />,
            color: '#a8edea',
            description: 'Sucursal'
        });
        setSucursalName(config[id]?.nombre || 'Sucursal Desconocida');
    }, [id]);

    const handleBack = () => {
        if (view !== 'menu') {
            setView('menu');
            return;
        }
        if (user.rol === 'admin') {
            navigate('/admin');
        } else {
            Swal.fire({
                title: '¿Cerrar Sesión?',
                text: 'Se perderá el progreso no guardado',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, salir',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#dc3545',
                cancelButtonColor: '#6c757d'
            }).then((res) => {
                if(res.isConfirmed) logout();
            });
        }
    };

    // Configuración de Módulos Base
    const todosLosModulos = [
        {
            key: 'ventas',
            label: 'PUNTO DE VENTA',
            sublabel: 'Facturación y Cobros',
            icon: <FaCashRegister />,
            gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            desc: 'Registrar ventas y gestionar caja',
            badge: 'Prioritario',
            badgeColor: '#28a745'
        },
        {
            key: 'productos',
            label: 'PRODUCTOS',
            sublabel: 'Catálogo y Menú',
            icon: <FaUtensils />,
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            desc: 'Administrar menú y precios',
            badge: null
        },
        {
            key: 'inventario',
            label: 'INVENTARIO',
            sublabel: 'Control de Stock',
            icon: <FaBoxOpen />,
            gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
            desc: 'Gestionar insumos y materias primas',
            badge: null
        },
        {
            key: 'mermas',
            label: 'MERMAS',
            sublabel: 'Desperdicios',
            icon: <FaTrashAlt />,
            gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            desc: 'Registrar pérdidas y desperdicios',
            badge: null
        },
        {
            key: 'egresos',
            label: 'EGRESOS',
            sublabel: 'Gastos Operativos',
            icon: <FaMoneyBillAlt />,
            gradient: 'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
            desc: 'Control de gastos y compras',
            badge: null
        },
        {
            key: 'misventas',
            label: 'REPORTES',
            sublabel: 'Análisis y Cierres',
            icon: <FaChartLine />,
            gradient: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
            desc: 'Historial, cierres y estadísticas',
            badge: null
        }
    ];

    // --- FILTRO DE PERMISOS (LOGICA NUEVA) ---
    // Si es ADMIN: Ve todo.
    // Si es CAJA: Solo ve 'ventas'.
    const modulosVisibles = user.rol === 'admin' 
        ? todosLosModulos 
        : todosLosModulos.filter(m => m.key === 'ventas');

    const renderActiveModule = () => {
        const props = { sucursalId: id, user: user, goBack: () => setView('menu') };
        switch (view) {
            case 'ventas': return <Ventas {...props} />;
            case 'productos': return <Productos {...props} />;
            case 'inventario': return <Inventario {...props} />;
            case 'egresos': return <Egresos {...props} />;
            case 'misventas': return <MisVentas {...props} />;
            case 'mermas': return <Mermas {...props} />;
            default: return null;
        }
    };

    if (view === 'menu') {
        return (
            <div className="min-vh-100 d-flex flex-column position-relative" style={{
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)',
                    backgroundSize: '40px 40px', zIndex: 0
                }}></div>

                {/* NAVBAR */}
                <nav className="px-4 py-3 position-relative" style={{
                    background: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(20px)',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                    zIndex: 10
                }}>
                    <div className="container-fluid">
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-3">
                                <div className="position-relative">
                                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle shadow-lg" style={{
                                        width: '70px', height: '70px', background: sucursalConfig.gradient,
                                        border: '3px solid rgba(255, 255, 255, 0.5)', position: 'relative', overflow: 'hidden'
                                    }}>
                                        <span style={{fontSize: '2.2rem', position: 'relative', zIndex: 2, color: 'white'}}>
                                            {sucursalConfig.icon}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="mb-0 fw-bold" style={{
                                        letterSpacing: '1.5px', textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        fontSize: '1.6rem', color: '#2c3e50'
                                    }}>
                                        {sucursalName}
                                    </h3>
                                    <div className="d-flex align-items-center gap-2 mt-1">
                                        <span className="badge" style={{
                                            background: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(10px)',
                                            color: '#2c3e50', padding: '0.4rem 0.8rem', borderRadius: '12px',
                                            fontSize: '0.75rem', border: '1px solid rgba(0, 0, 0, 0.1)'
                                        }}>
                                            {sucursalConfig.description}
                                        </span>
                                        <span className="badge bg-success" style={{padding: '0.4rem 0.8rem', borderRadius: '12px', fontSize: '0.75rem'}}>
                                            ● Operativo
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button 
                                className="btn fw-bold shadow"
                                style={{
                                    background: 'white', border: '1px solid rgba(0, 0, 0, 0.1)',
                                    color: '#2c3e50', borderRadius: '35px', padding: '14px 35px',
                                    transition: 'all 0.3s ease', fontSize: '0.95rem', letterSpacing: '0.5px'
                                }}
                                onClick={handleBack}
                            >
                                {user.rol === 'admin' ? 
                                    <><FaArrowLeft className="me-2"/> Panel Admin</> : 
                                    <><FaSignOutAlt className="me-2"/> Cerrar Sesión</>
                                }
                            </button>
                        </div>
                    </div>
                </nav>

                <div className="container flex-grow-1 d-flex align-items-center justify-content-center py-5 position-relative" style={{zIndex: 1}}>
                    <div className="w-100">
                        {/* HEADER DASHBOARD */}
                        <div className="text-center mb-5 position-relative" style={{ animation: 'fadeInDown 1s ease-out' }}>
                            <div className="mb-4 position-relative d-inline-block">
                                <div style={{
                                    position: 'absolute', width: '160px', height: '160px', borderRadius: '50%',
                                    background: sucursalConfig.gradient, opacity: '0.15', top: '50%', left: '50%',
                                    transform: 'translate(-50%, -50%)', animation: 'pulse 3s ease-in-out infinite', filter: 'blur(20px)'
                                }}></div>
                                <div className="d-inline-flex align-items-center justify-content-center rounded-circle shadow-lg position-relative"
                                    style={{
                                        width: '140px', height: '140px', background: sucursalConfig.gradient,
                                        border: '6px solid rgba(255, 255, 255, 0.8)',
                                        boxShadow: `0 20px 60px ${sucursalConfig.color}30`
                                    }}
                                >
                                    <span style={{fontSize: '4rem', color: 'white'}}>{sucursalConfig.icon}</span>
                                </div>
                            </div>
                            <h1 className="fw-bold mb-3" style={{ fontSize: '3.5rem', color: '#2c3e50', letterSpacing: '3px' }}>PANEL DE CONTROL</h1>
                            <div style={{
                                position: 'absolute', bottom: '-10px', left: '50%', transform: 'translateX(-50%)',
                                width: '60%', height: '3px', background: sucursalConfig.gradient, borderRadius: '10px'
                            }}></div>
                            <p className="lead mb-4" style={{ fontSize: '1.3rem', color: '#5a6c7d' }}>Seleccione un módulo para comenzar</p>
                        </div>

                        {/* TARJETAS DE MÓDULOS (FILTRADAS POR ROL) */}
                        <div className="row g-4 justify-content-center pb-5">
                            {modulosVisibles.map((mod, index) => (
                                <div key={mod.key} className="col-xl-3 col-lg-4 col-md-6">
                                    <div 
                                        className="card h-100 border-0 position-relative overflow-hidden"
                                        style={{
                                            cursor: 'pointer', borderRadius: '30px',
                                            background: 'rgba(255, 255, 255, 0.85)',
                                            backdropFilter: 'blur(20px)',
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                                            transform: 'translateY(0)',
                                            transition: 'all 0.3s ease',
                                            animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                                        }}
                                        onClick={() => setView(mod.key)}
                                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-10px)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                                    >
                                        <div style={{ height: '4px', background: mod.gradient }}></div>
                                        {mod.badge && (
                                            <div className="position-absolute top-0 end-0 m-3">
                                                <span className="badge shadow-lg" style={{ background: mod.badgeColor, borderRadius: '20px' }}>⚡ {mod.badge}</span>
                                            </div>
                                        )}
                                        <div className="card-body p-4 text-center">
                                            <div className="mb-3 d-inline-flex align-items-center justify-content-center rounded-circle shadow-lg" 
                                                 style={{ width: '80px', height: '80px', background: mod.gradient, border: '3px solid white' }}>
                                                <div style={{fontSize: '2.5rem', color: 'white'}}>{mod.icon}</div>
                                            </div>
                                            <h5 className="fw-bold mb-2" style={{color: '#2c3e50'}}>{mod.label}</h5>
                                            <p className="mb-3 small" style={{color: '#7f8c8d'}}>{mod.desc}</p>
                                            <div className="btn btn-sm fw-bold w-100" style={{ background: 'rgba(0,0,0,0.05)', borderRadius: '20px' }}>ACCEDER →</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* FOOTER */}
                        <div className="text-center mt-2 pb-5">
                            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '500px', borderRadius: '30px', background: 'rgba(255, 255, 255, 0.8)' }}>
                                <div className="card-body py-3">
                                    <div className="d-flex align-items-center justify-content-center gap-3">
                                        <div style={{width:'40px', height:'40px', background: sucursalConfig.gradient, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', color:'white'}}>
                                            {user.nombre.charAt(0)}
                                        </div>
                                        <div className="text-start">
                                            <small className="d-block text-muted" style={{fontSize: '0.7rem'}}>USUARIO ACTIVO</small>
                                            <span className="fw-bold text-dark">{user.nombre}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <style jsx>{`
                    @keyframes pulse { 0% { transform: translate(-50%, -50%) scale(1); opacity: 0.15; } 50% { transform: translate(-50%, -50%) scale(1.15); opacity: 0.25; } 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.15; } }
                    @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
                `}</style>
            </div>
        );
    }
    return (
        <div className="container-fluid p-0 min-vh-100 bg-white">
            {renderActiveModule()}
        </div>
    );
}